import { Car, ClipboardList, Mic, FileCheck2, ShieldCheck, Lock, ListChecks, FileWarning } from "lucide-react";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";

const STEPS = [
  {
    n: "01",
    icon: Car,
    title: "Leave the Visit",
    desc: "Capture the encounter right from the car or office — while the detail, observations, and responses are completely fresh.",
  },
  {
    n: "02",
    icon: ClipboardList,
    title: "Open the Role Prompt",
    desc: "Nurse, therapist, specialist, peer — each prompt asks for exactly the fields that role must document.",
  },
  {
    n: "03",
    icon: Mic,
    title: "Speak the Contact",
    desc: "Who (but never by name), where, goal worked, what you did, how they responded, risk, next step.",
  },
  {
    n: "04",
    icon: FileCheck2,
    title: "Review and Paste",
    desc: "Edit anything that is off. Drop the note into your existing chart, in the format you choose.",
  },
];

const FORMATS = [
  { abbr: "PIE", full: "Problem · Intervention · Evaluation" },
  { abbr: "SOAP", full: "Subjective · Objective · Assessment · Plan" },
  { abbr: "DAP", full: "Data · Assessment · Plan" },
];

const GUARDRAILS = [
  {
    icon: ShieldCheck,
    title: "The clinician owns the note",
    desc: "AI drafts it; you own it. Always review and edit before it hits the official record.",
  },
  {
    icon: Lock,
    title: "No PHI required",
    desc: "Demos use fake scenarios. Real use stays compliant inside your agency's approved AI tools.",
  },
  {
    icon: ListChecks,
    title: "Built for community-based documentation requirements",
    desc: "Designed to capture goals, interventions, responses, and next steps rather than vague session summaries.",
  },
  {
    icon: FileWarning,
    title: "Does not replace fidelity",
    desc: "Faster notes must still be factual. VeraChart accelerates your documentation, not fabrication.",
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-white py-20 lg:py-28" aria-labelledby="how-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <Eyebrow>The Workflow</Eyebrow>
          <SectionHeading id="how-heading">Four Steps. No New EHR. No New Login Maze.</SectionHeading>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            We install role-specific prompt templates into your agency's approved AI platform — Google Workspace,
            Microsoft Copilot, or ChatGPT — and train your team live. After that, a note looks like this:
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="workflow-steps">
          {STEPS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.1}>
              <div className="group relative h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:border-teal-300 hover:shadow-xl hover:shadow-navy/5">
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white transition-colors group-hover:bg-teal-600">
                    <step.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-mono text-4xl font-bold text-slate-100 transition-colors group-hover:text-teal-100">{step.n}</span>
                </div>
                <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-navy">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="format-chips">
            <span className="text-sm font-medium text-slate-500">Supported documentation structures:</span>
            {FORMATS.map((f) => (
              <span key={f.abbr} className="rounded-full border border-slate-300 bg-slate-50 px-4 py-1.5 text-sm text-navy">
                <span className="font-mono font-bold text-teal-700">{f.abbr}</span>
                <span className="ml-2 hidden text-slate-500 sm:inline">{f.full}</span>
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="guardrails-block">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">What This Is Not</p>
            <h3 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              A draft is not a signature. A prompt is not the chart.
            </h3>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {GUARDRAILS.map((g, i) => (
                <div key={g.title} className="h-full rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-colors hover:border-teal-300">
                  <div className="flex items-center justify-between">
                    <g.icon className="h-6 w-6 text-teal-700" aria-hidden="true" />
                    <span className="font-mono text-sm font-bold text-slate-300">0{i + 1}</span>
                  </div>
                  <h4 className="mt-4 font-display text-base font-bold text-navy">{g.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
